import { Box, Typography, Stack, Button, Paper } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import transactionImage from "../assets/create.png";
import analyticsImage from "../assets/share.png";
import analyzeImg from "../assets/analyze.png";

export default function HowThisWorks() {
    const sections = [
        {
          icon: PersonAddIcon,
          title: "Create an Account",
          description:
            "Sign up in seconds and get started with your expense tracking journey. Simply provide your email and create a secure password to begin managing your finances effectively.",
          image: transactionImage,
          steps: [
            "Visit our website or download the app",
            "Click 'Get Started' or 'Sign Up'",
            "Enter your email and create a password",
            "Verify your email address",
            "Start tracking your expenses immediately",
          ],
          showButton: true,
        },
        {
          icon: ReceiptLongIcon,
          title: "Track Expenses",
          description:
            "Easily log your daily expenses and categorize them for better insights. Our intuitive interface makes it simple to record every transaction and understand your spending patterns.",
          image: analyzeImg,
          steps: [
            "Click the '+' button to add a new expense",
            "Enter the transaction details and amount",
            "Select or create a category",
            "Add notes for future reference (optional)",
            "Save and view your updated balance",
          ],
        },
        {
          icon: SavingsIcon,
          title: "Set Budgets & Analyze",
          description:
            "Define spending limits and get notified when you are approaching them. Take control of your financial goals with smart budget alerts and detailed analytics insights.",
          image: analyticsImage,
          steps: [
            "Navigate to the Budget section",
            "Set monthly or weekly spending limits",
            "Assign budgets to specific categories",
            "Enable notifications for budget alerts",
            "Monitor your progress with visual charts in real-time",
          ],
        },
        {
          icon: DeleteOutlineIcon,
          title: "How to Delete Your Account",
          description:
            "We respect your privacy and data. If you wish to delete your account, follow these simple steps. Please note that this action is permanent and will remove all your data.",
          image:
            "https://images.unsplash.com/photo-1758983308742-f4ba1f8c8cb4?auto=format&fit=crop&w=500&q=80",
          steps: [
            "Log in to your Spendifyr account",
            "Go to Settings from the menu",
            "Scroll down to 'Account Management'",
            "Click 'Delete Account' button",
            "Confirm deletion and provide feedback (optional)",
            "Your account will be permanently deleted within 24 hours",
          ],
          warning: true,
        },
      ];
      

  return (
    <Box id="how-this-works" sx={{ py: 10, px: { xs: 2, md: 6 } }}>
      <Box maxWidth="1200px" mx="auto">
        {/* Header */}
        <Box textAlign="center" mb={8}>
          <Typography variant="h4" fontWeight={600} color="text.primary" mb={2}>
            How This Works
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth="600px" mx="auto">
            Get started with our simple process and take control of your finances today.
          </Typography>
        </Box>

        {/* Sections */}
        <Stack spacing={12}>
          {sections.map((section, index) => {
            const isReversed = index % 2 === 1;

            return (
              <Stack
                key={index}
                direction={{ xs: "column", md: isReversed ? "row-reverse" : "row" }}
                spacing={6}
                alignItems="center"
              >
                {/* TEXT COLUMN */}
                <Box flex={1}>
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        backgroundColor: "rgba(46, 94, 58, 0.12)",
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <section.icon size={32} color="#2E5E3A" />
                    </Box>

                    <Typography variant="h5" fontWeight={600} color="text.primary">
                      {section.title}
                    </Typography>
                  </Stack>

                  <Typography color="text.secondary" mb={3}>
                    {section.description}
                  </Typography>

                  {section.warning && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        border: "1px solid #f5c2c7",
                        backgroundColor: "#f8d7da",
                      }}
                    >
                      <Typography color="#842029" fontSize="14px">
                        <strong>Warning:</strong> Account deletion is permanent and cannot be
                        undone. All your data will be permanently removed.
                      </Typography>
                    </Paper>
                  )}

                  {/* STEPS */}
                  <Stack spacing={2} mt={2}>
                    {section.steps.map((step, i) => (
                      <Stack direction="row" spacing={2} key={i}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: "#2E5E3A",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Typography color="text.primary">{step}</Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Button on First Section */}
                  {section.showButton && (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 3,
                        backgroundColor: "#2E5E3A",
                        borderRadius: "30px",
                        px: 4,
                        py: 1.5,
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#234a2d" },
                      }}
                      onClick={() =>
                        document
                          .getElementById("contact-us")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      Get Started Now
                    </Button>
                  )}
                </Box>

                {/* IMAGE COLUMN */}
                <Box flex={1} display="flex" justifyContent="center">
                  <Box sx={{ position: "relative" }}>
                    {/* Glow Background */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(135deg, rgba(46,94,58,0.2), rgba(0,200,0,0.2))",
                        filter: "blur(45px)",
                        borderRadius: "30px",
                        transform: "scale(0.9)",
                      }}
                    />

                    {/* Image */}
                    <Box
                      component="img"
                      src={section.image}
                      alt={section.title}
                      sx={{
                        width: "100%",
                        maxWidth: "450px",
                        height: "600px",
                        objectFit: "cover",
                        borderRadius: "30px",
                        position: "relative",
                        zIndex: 1,
                        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.2)",
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
